import { Link } from 'react-router-dom';
import { formatCpmCount } from '../../../constants/centralProductMaster.constants';
import type { CpmStatusCountMap } from '../../../types/centralProductMaster.types';
import SectionError from './SectionError';

interface LinkDecisionsPanelProps {
    productLinks: CpmStatusCountMap;
    barcodeConflictsCount: number | null;
    linksError?: boolean;
    barcodeError?: boolean;
    onRetryLinks?: () => void;
    onRetryBarcode?: () => void;
    retryingLinks?: boolean;
    retryingBarcode?: boolean;
}

const OUTCOMES: Array<{
    key: string;
    label: string;
    color: string;
    warn?: boolean;
    muted?: boolean;
    href?: string;
}> = [
    {
        key: 'LINKED_EXISTING',
        label: 'Linked to existing product',
        color: 'var(--cpm-success-dot)',
    },
    {
        key: 'CREATED_NEW',
        label: 'New product created',
        color: 'var(--cpm-brand-purple)',
    },
    {
        key: 'NEEDS_REVIEW',
        label: 'Needs review',
        color: 'var(--cpm-warning-dot)',
        warn: true,
        href: '/central-product-master/verification-queue?tab=link',
    },
    {
        key: 'REJECTED',
        label: 'Rejected',
        color: 'var(--cpm-text-tertiary)',
        muted: true,
    },
];

export default function LinkDecisionsPanel({
    productLinks,
    barcodeConflictsCount,
    linksError = false,
    barcodeError = false,
    onRetryLinks,
    onRetryBarcode,
    retryingLinks = false,
    retryingBarcode = false,
}: LinkDecisionsPanelProps) {
    const by = productLinks.byStatus ?? {};
    const infraFailed = by.INFRA_FAILED ?? 0;
    const total = productLinks.total ?? 0;

    return (
        <div
            className="bg-[var(--cpm-surface-card)] border border-[var(--cpm-border-default)] rounded-[16px] shadow-[var(--cpm-shadow-card)] flex flex-col"
            style={{ padding: 20, gap: 18 }}
        >
            <div className="flex items-baseline" style={{ gap: 10 }}>
                <h2 className="m-0 !text-[14px] !font-extrabold !text-[var(--cpm-text-primary)]">
                    Link decisions
                </h2>
                <span
                    className="text-[var(--cpm-text-secondary)]"
                    style={{ fontSize: 12, fontWeight: 600 }}
                >
                    All ProductLink records, by outcome
                </span>
                {!linksError ? (
                    <span
                        className="ml-auto tabular text-[var(--cpm-text-secondary)]"
                        style={{ fontSize: 12, fontWeight: 800 }}
                    >
                        {formatCpmCount(total)} total
                    </span>
                ) : null}
            </div>

            {linksError ? (
                <SectionError
                    title="Couldn't load link decisions"
                    body="The ProductLink aggregate did not return. Nothing has changed — retry to load the breakdown."
                    onRetry={onRetryLinks}
                    retrying={retryingLinks}
                />
            ) : null}

            {!linksError && infraFailed > 0 ? (
                <div
                    className="flex items-center rounded-[12px]"
                    style={{
                        backgroundImage: 'var(--cpm-gradient-hero)',
                        padding: '18px 20px',
                        gap: 18,
                    }}
                >
                    <div className="flex flex-col shrink-0" style={{ gap: 4 }}>
                        <span
                            className="uppercase"
                            style={{
                                fontSize: 10.5,
                                fontWeight: 800,
                                letterSpacing: '0.08em',
                                color: 'rgba(255,255,255,.65)',
                            }}
                        >
                            Left over from the last run
                        </span>
                        <span
                            className="tabular text-white"
                            style={{
                                fontSize: 38,
                                fontWeight: 800,
                                letterSpacing: '-0.025em',
                                lineHeight: 1,
                            }}
                        >
                            {formatCpmCount(infraFailed)}
                        </span>
                        <span
                            className="font-[family-name:var(--cpm-font-mono)]"
                            style={{
                                fontSize: 10,
                                fontWeight: 700,
                                letterSpacing: '0.04em',
                                color: 'rgba(255,255,255,.5)',
                            }}
                        >
                            INFRA_FAILED
                        </span>
                    </div>
                    <div
                        className="self-stretch"
                        style={{ width: 1, background: 'rgba(255,255,255,.18)' }}
                    />
                    <div className="flex flex-col" style={{ gap: 8 }}>
                        <span
                            style={{
                                fontSize: 14,
                                fontWeight: 600,
                                lineHeight: 1.5,
                                color: 'rgba(255,255,255,.85)',
                            }}
                        >
                            The last linking run finished adjudicating, but{' '}
                            {formatCpmCount(infraFailed)} items never reached a final decision —
                            repeated infrastructure errors exhausted their automatic retries. They
                            stay unlinked until someone dismisses and requeues them by retrying that
                            run.
                        </span>
                        <a
                            href="#batch-jobs"
                            className="inline-flex items-center self-start rounded-full no-underline !text-white !text-[11.5px] !font-extrabold"
                            style={{
                                gap: 7,
                                padding: '4px 10px',
                                background: 'rgba(255,255,255,.12)',
                                border: '1px solid rgba(255,255,255,.2)',
                            }}
                            onClick={(e) => {
                                e.preventDefault();
                                document
                                    .getElementById('batch-jobs')
                                    ?.scrollIntoView({ behavior: 'smooth' });
                            }}
                        >
                            <span
                                style={{
                                    width: 6,
                                    height: 6,
                                    borderRadius: 9999,
                                    background: '#FBBF24',
                                }}
                            />
                            Retry the run from Batch job health →
                        </a>
                    </div>
                </div>
            ) : null}

            {!linksError ? (
                <div className="flex flex-col">
                    {OUTCOMES.map((row, i) => {
                        const count = by[row.key] ?? 0;
                        const widthPct = total > 0 ? (count / total) * 100 : 0;
                        const isLast = i === OUTCOMES.length - 1;
                        const content = (
                            <>
                                <div
                                    className="flex flex-col shrink-0"
                                    style={{ gap: 3, width: 200 }}
                                >
                                    <span
                                        style={{
                                            fontSize: 13,
                                            fontWeight: 700,
                                            color: row.warn
                                                ? 'var(--cpm-warning-text)'
                                                : row.muted
                                                  ? 'var(--cpm-text-secondary)'
                                                  : undefined,
                                        }}
                                    >
                                        {row.label}
                                    </span>
                                    <span
                                        className="font-[family-name:var(--cpm-font-mono)] text-[var(--cpm-text-tertiary)]"
                                        style={{
                                            fontSize: 10,
                                            fontWeight: 700,
                                            letterSpacing: '0.04em',
                                        }}
                                    >
                                        {row.key}
                                    </span>
                                </div>
                                <div
                                    className="flex-1 rounded-full overflow-hidden bg-[var(--cpm-surface-skeleton)]"
                                    style={{ height: 8 }}
                                >
                                    <span
                                        style={{
                                            display: 'block',
                                            width: `${widthPct}%`,
                                            height: '100%',
                                            background: row.color,
                                        }}
                                    />
                                </div>
                                <span
                                    className="tabular"
                                    style={{
                                        width: 70,
                                        textAlign: 'right',
                                        fontSize: 15,
                                        fontWeight: 800,
                                        color: row.warn
                                            ? 'var(--cpm-warning-text)'
                                            : row.muted
                                              ? 'var(--cpm-text-secondary)'
                                              : undefined,
                                    }}
                                >
                                    {formatCpmCount(count)}
                                </span>
                            </>
                        );
                        const rowClass = [
                            'flex items-center',
                            isLast ? '' : 'border-b border-[var(--cpm-border-default)]',
                        ].join(' ');
                        const rowStyle = { gap: 12, padding: '12px 0' as const };
                        if (row.href) {
                            return (
                                <Link
                                    key={row.key}
                                    to={row.href}
                                    className={[rowClass, 'no-underline !text-inherit'].join(' ')}
                                    style={rowStyle}
                                >
                                    {content}
                                </Link>
                            );
                        }
                        return (
                            <div key={row.key} className={rowClass} style={rowStyle}>
                                {content}
                            </div>
                        );
                    })}
                </div>
            ) : null}

            {barcodeError || barcodeConflictsCount == null ? (
                <div
                    className="flex items-center rounded-[10px] bg-[var(--cpm-shortfall-bg)] border border-[var(--cpm-shortfall-border)]"
                    style={{ gap: 13, padding: '14px 15px' }}
                >
                    <div className="flex flex-col min-w-0 flex-1" style={{ gap: 3 }}>
                        <span
                            className="text-[var(--cpm-shortfall-text)]"
                            style={{ fontSize: 13.5, fontWeight: 800 }}
                        >
                            Couldn&apos;t load barcode conflicts
                        </span>
                        <span
                            className="text-[var(--cpm-shortfall-text-med)]"
                            style={{ fontSize: 12.5, fontWeight: 600, lineHeight: 1.4 }}
                        >
                            The conflict count is unavailable — the rest of this card is current.
                        </span>
                    </div>
                    {onRetryBarcode ? (
                        <button
                            type="button"
                            disabled={retryingBarcode}
                            onClick={onRetryBarcode}
                            className="shrink-0 border-0 bg-transparent cursor-pointer text-[var(--cpm-shortfall-text)]"
                            style={{ fontSize: 12, fontWeight: 800 }}
                        >
                            {retryingBarcode ? 'Retrying…' : 'Retry'}
                        </button>
                    ) : null}
                </div>
            ) : barcodeConflictsCount > 0 ? (
                <Link
                    to="/central-product-master/verification-queue?tab=barcode"
                    className="flex items-center rounded-[10px] no-underline !text-inherit bg-[var(--cpm-shortfall-bg)] border border-[var(--cpm-shortfall-border)]"
                    style={{ gap: 13, padding: '14px 15px' }}
                >
                    <span
                        className="shrink-0 flex items-center justify-center bg-white text-[var(--cpm-shortfall-icon)]"
                        style={{
                            width: 30,
                            height: 30,
                            borderRadius: 8,
                            border: '1px solid var(--cpm-shortfall-border)',
                        }}
                    >
                        <svg
                            width="15"
                            height="15"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="2"
                        >
                            <path d="M3 5v14" />
                            <path d="M8 5v14" />
                            <path d="M12 5v14" />
                            <path d="M17 5v14" />
                            <path d="M21 5v14" />
                        </svg>
                    </span>
                    <div className="flex flex-col min-w-0" style={{ gap: 3 }}>
                        <span
                            className="text-[var(--cpm-shortfall-text)]"
                            style={{ fontSize: 13.5, fontWeight: 800 }}
                        >
                            Barcode conflicts
                        </span>
                        <span
                            className="text-[var(--cpm-shortfall-text-med)]"
                            style={{ fontSize: 12.5, fontWeight: 600, lineHeight: 1.4 }}
                        >
                            The same barcode was seen pointing at two different master products — a
                            human has to pick the right one.
                        </span>
                    </div>
                    <span className="ml-auto flex items-baseline shrink-0" style={{ gap: 6 }}>
                        <span
                            className="tabular text-[var(--cpm-shortfall-text-med)]"
                            style={{
                                fontSize: 26,
                                fontWeight: 800,
                                letterSpacing: '-0.025em',
                                lineHeight: 1,
                            }}
                        >
                            {formatCpmCount(barcodeConflictsCount)}
                        </span>
                        <span
                            className="uppercase text-[var(--cpm-shortfall-text-med)]"
                            style={{ fontSize: 10.5, fontWeight: 800, letterSpacing: '0.04em' }}
                        >
                            Conflicted
                        </span>
                    </span>
                </Link>
            ) : (
                <div
                    className="flex items-center rounded-[10px] bg-[var(--cpm-success-bg)] border border-[var(--cpm-success-border)]"
                    style={{ gap: 13, padding: '14px 15px' }}
                >
                    <span
                        className="shrink-0 flex items-center justify-center bg-white text-[var(--cpm-success-text-light)]"
                        style={{
                            width: 30,
                            height: 30,
                            borderRadius: 8,
                            border: '1px solid var(--cpm-success-border)',
                        }}
                    >
                        <svg
                            width="15"
                            height="15"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="2.2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                        >
                            <path d="M20 6 9 17l-5-5" />
                        </svg>
                    </span>
                    <div className="flex flex-col min-w-0" style={{ gap: 3 }}>
                        <span
                            className="text-[var(--cpm-success-text-light)]"
                            style={{ fontSize: 13.5, fontWeight: 800 }}
                        >
                            No barcode conflicts
                        </span>
                        <span
                            className="text-[var(--cpm-text-secondary)]"
                            style={{ fontSize: 12.5, fontWeight: 600, lineHeight: 1.4 }}
                        >
                            Every barcode points at a single master product.
                        </span>
                    </div>
                </div>
            )}
        </div>
    );
}
